/**
 * AuiFilePreview
 *
 * Inline preview pane for one VFS entry. Routes by MIME / extension to the
 * right renderer (markdown / text / csv / image / video / pdf / fallback).
 * Header shows the filename, a 3-dot menu (Download / Delete) and a close
 * button. The metadata strip (path · size · type) auto-hides when the pane
 * narrows below ~360px.
 *
 * Reuses the type-router logic from `pages/knowledge/DocumentViewer`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ChevronRight, Download, FileText, ImageOff, Loader2, MoreVertical, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { JsonContent } from '@/components/ui/json-content';
import { CodeContent, languageFromExt } from '@/components/ui/code-content';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AuiTable } from './AuiTable';
import type { AuiNode } from '@/types/canvases';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { formatFileSize, formatAppleDate } from '@/utils/format';
import { downloadFileFromUrl } from '@/utils/download-file';
import { useToast } from '@/components/ui/Toast';
import { CopyButton } from '@/components/ui/CopyButton';
import { cn } from '@/lib/cn';
import type { VfsEntry } from '@/types/vfs';
import type { VfsDerivative } from '@/types/kb';
import type { FilesBackend, FilesEntry } from './files-backend';

/**
 * Derivative kinds the backend emits for internal/raw consumption that
 * should never surface as a preview tab. `ocrRaw` / `textExtractRaw` carry
 * the full Textract Blocks JSON (geometry, bounding boxes, confidence) —
 * useful later for bounding-box overlays, but redundant here since we
 * already show the extracted `ocr` text. Hidden, not dropped: the data
 * stays on the entry for future features to consume.
 */
const HIDDEN_DERIVATIVE_TYPES = new Set(['ocrRaw', 'textExtractRaw']);

/** Derivatives that should be shown as preview tabs (raw OCR excluded). */
function visibleDerivatives(entry: FilesEntry): VfsDerivative[] {
  return (entry.derivatives ?? []).filter(
    (d): d is VfsDerivative => Boolean(d) && !HIDDEN_DERIVATIVE_TYPES.has(d.type),
  );
}

/**
 * Format a derivative into a user-friendly tab label. Driven by the
 * derivative's `contentType` — so an OCR derivative (type=`ocr`,
 * contentType=`text/plain`) shows up as "Text", and an image
 * thumbnail (contentType=`image/png`) shows up as "Image". Falls back
 * to a title-cased version of the raw `type` when the content-type
 * is missing.
 */
function derivativeLabel(d: VfsDerivative): string {
  const ct = (d.contentType ?? '').toLowerCase();
  if (ct === 'text/markdown') return 'Markdown';
  if (ct === 'text/csv') return 'CSV';
  if (ct === 'text/html') return 'HTML';
  if (ct === 'application/json') return 'JSON';
  if (ct === 'application/xml' || ct === 'text/xml') return 'XML';
  if (ct === 'application/pdf') return 'PDF';
  if (ct.startsWith('text/')) return 'Text';
  if (ct.startsWith('image/')) return 'Image';
  if (ct.startsWith('video/')) return 'Video';
  if (ct.startsWith('audio/')) return 'Audio';
  // Unknown / missing content type — title-case the raw `type` segment.
  return d.type
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


/**
 * Fetch a file's body as text via the supplied backend and keep it in
 * sync as the entry version changes. On a same-file refresh
 * (vfs.file.updated, fileId unchanged) the previous content stays
 * visible while the new body fetches — only the data swap is
 * observable, no flash to a loader. Switching to a different file
 * blanks content so the user sees a proper loading state instead of
 * stale data.
 */
function useFilesEntryText(backend: FilesBackend, entry: VfsEntry): {
  content: string | null;
  error: Error | null;
} {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const lastFileIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (lastFileIdRef.current !== entry.fileId) {
      setContent(null);
    }
    lastFileIdRef.current = entry.fileId;
    backend
      .readText(entry.path, entry.updatedAt)
      .then((text) => { if (!cancelled) setContent(text); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err : new Error(String(err))); });
    return () => { cancelled = true; };
  }, [backend, entry.fileId, entry.path, entry.updatedAt]);

  return { content, error };
}

type ContentCategory = 'pdf' | 'image' | 'video' | 'markdown' | 'csv' | 'json' | 'text' | 'unknown';

function normalizeType(mimeType: string, name: string): ContentCategory {
  const mt = mimeType.toLowerCase();
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (mt === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mt.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (mt.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov'].includes(ext)) return 'video';
  if (mt === 'text/markdown' || ext === 'md' || ext === 'markdown') return 'markdown';
  if (mt === 'text/csv' || ext === 'csv') return 'csv';
  if (mt === 'application/json' || ext === 'json') return 'json';
  if (
    mt === 'text/plain' ||
    mt.startsWith('text/') ||
    ['txt', 'log', 'xml', 'yaml', 'yml', 'sh', 'js', 'ts', 'tsx', 'jsx', 'py', 'rb', 'go', 'rs', 'css', 'html', 'sql'].includes(ext)
  ) return 'text';
  return 'unknown';
}

export interface AuiFilePreviewProps {
  backend: FilesBackend;
  entry: VfsEntry;
  onClose: () => void;
  onDelete?: () => Promise<void> | void;
  allowDelete?: boolean;
  className?: string;
}

export function AuiFilePreview({
  backend,
  entry,
  onClose,
  onDelete,
  allowDelete,
  className,
}: AuiFilePreviewProps) {
  const toast = useToast();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<Error | null>(null);
  const type = normalizeType(entry.contentType, entry.name);
  // Tab state lives at the preview level so the footer can react to
  // which derivative is being shown. Defaults to the original each
  // time the entry changes.
  const [activeTab, setActiveTab] = useState<string>('source');
  useEffect(() => {
    setActiveTab('source');
  }, [entry.fileId]);
  const derivatives = visibleDerivatives(entry as FilesEntry);
  const activeDerivative = derivatives.find((d) => d.fileId === activeTab) ?? null;

  // Resolve a presigned download URL each time the entry's identity OR
  // version changes. On a same-file refresh (vfs.file.updated) we keep
  // the existing URL visible until the new one arrives — that prevents
  // the image / pdf / video from blanking to a loader between updates.
  // On a switch to a different file, the URL IS blanked so the user
  // sees a loading state instead of stale content.
  const downloadFileIdRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setUrlError(null);
    if (downloadFileIdRef.current !== entry.fileId) {
      setDownloadUrl(null);
    }
    downloadFileIdRef.current = entry.fileId;
    backend
      .getDownloadUrl(entry.path)
      .then((url) => { if (!cancelled) setDownloadUrl(url); })
      .catch((err) => { if (!cancelled) setUrlError(err instanceof Error ? err : new Error(String(err))); });
    return () => { cancelled = true; };
  }, [backend, entry.fileId, entry.path, entry.updatedAt]);

  const hasMenuItems = Boolean(downloadUrl) || (allowDelete && onDelete);

  return (
    <div
      className={cn('group flex h-full min-h-0 min-w-0 flex-col bg-card', className)}
      data-testid="aui-file-preview"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <span className="truncate text-sm font-medium text-foreground flex-1" title={entry.name}>
          {entry.name}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          title="Close preview"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        {hasMenuItems && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="File actions"
                title="File actions"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {downloadUrl && (
                <DropdownMenuItem
                  onSelect={async () => {
                    try {
                      await downloadFileFromUrl(downloadUrl, entry.name);
                    } catch {
                      toast.error(`Failed to download ${entry.name}`);
                    }
                  }}
                >
                  <Download className="h-4 w-4 text-muted-foreground" />
                  Download
                </DropdownMenuItem>
              )}
              {allowDelete && onDelete && (
                <>
                  {downloadUrl && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onSelect={() => void onDelete()}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <PreviewBody
        backend={backend}
        entry={entry}
        type={type}
        downloadUrl={downloadUrl}
        urlError={urlError}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <PreviewFooter entry={entry} activeDerivative={activeDerivative} />
    </div>
  );
}

function PreviewFooter({
  entry,
  activeDerivative,
}: {
  entry: VfsEntry;
  activeDerivative: VfsDerivative | null;
}) {
  // When a derivative tab is active we surface ITS metadata (type,
  // size, created) — the source values aren't relevant for the
  // alternate view. Modified time isn't tracked on derivatives, so we
  // hide that row.
  const contentType = activeDerivative
    ? activeDerivative.contentType ?? 'unknown'
    : entry.contentType || 'unknown';
  const size = activeDerivative
    ? activeDerivative.sizeBytes ?? 0
    : entry.size;
  const created = activeDerivative ? activeDerivative.createdAt : entry.createdAt;
  const updated = activeDerivative ? null : entry.updatedAt;
  const sameTimestamp = created === updated;
  return (
    <footer className="flex items-center gap-2 overflow-hidden border-t border-border bg-card/60 px-4 py-1.5 text-xs text-muted-foreground">
      {/* Path + (optional derivative breadcrumb) + copy button.
       *  Copying always yields the original file path — the
       *  derivative is just a view of it, not an addressable entity
       *  the user would paste elsewhere. */}
      <span className="flex min-w-0 max-w-full items-center gap-1">
        <code
          className="min-w-0 truncate font-mono text-foreground/90"
          title={entry.path}
        >
          {entry.path}
        </code>
        {activeDerivative && (
          <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
            <ChevronRight aria-hidden className="h-3 w-3" />
            <span className="font-mono">{derivativeLabel(activeDerivative)}</span>
          </span>
        )}
        <CopyButton
          value={entry.path}
          title="Copy path"
          className="shrink-0 text-muted-foreground hover:text-foreground"
        />
      </span>
      {/* Right-aligned metadata group. `ml-auto` pushes it to the far
       *  right; the path group on the left grows/shrinks into whatever
       *  space is left. */}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        <span className="inline-flex items-baseline gap-1.5 truncate">
          <span>Type</span>
          <span className="text-foreground/90">{contentType}</span>
        </span>
        <span aria-hidden className="text-muted-foreground/40">·</span>
        <span className="inline-flex items-baseline gap-1.5">
          <span>Size</span>
          <span className="tabular-nums text-foreground/90">
            {formatFileSize(size)}
          </span>
        </span>
        {created && (
          <>
            <span aria-hidden className="text-muted-foreground/40">·</span>
            <span className="inline-flex items-baseline gap-1.5">
              <span>Created</span>
              <span className="text-foreground/90">{formatAppleDate(created)}</span>
            </span>
          </>
        )}
        {updated && !sameTimestamp && (
          <>
            <span aria-hidden className="text-muted-foreground/40">·</span>
            <span className="inline-flex items-baseline gap-1.5">
              <span>Modified</span>
              <span className="text-foreground/90">{formatAppleDate(updated)}</span>
            </span>
          </>
        )}
      </span>
    </footer>
  );
}

function PreviewBody({
  backend,
  entry,
  type,
  downloadUrl,
  urlError,
  activeTab,
  onTabChange,
}: {
  backend: FilesBackend;
  entry: VfsEntry;
  type: ContentCategory;
  downloadUrl: string | null;
  urlError: Error | null;
  activeTab: string;
  onTabChange: (tab: string) => void;
}) {
  const derivatives = visibleDerivatives(entry as FilesEntry);
  const tab = activeTab;
  const setTab = onTabChange;

  const renderSource = () => {
    // The presigned URL is only needed for binary renderers (pdf /
    // image / video); text-based renderers fetch their body via the
    // backend.readText path so urlError must not block them.
    switch (type) {
      case 'pdf':
        return urlError ? (
          <StatusBlock icon={<AlertCircle className="h-8 w-8" />} message="Failed to load file" />
        ) : downloadUrl ? <PdfRenderer url={downloadUrl} /> : <Loading />;
      case 'image':
        return urlError ? (
          <StatusBlock icon={<AlertCircle className="h-8 w-8" />} message="Failed to load file" />
        ) : downloadUrl ? <ImageRenderer url={downloadUrl} name={entry.name} /> : <Loading />;
      case 'video':
        return urlError ? (
          <StatusBlock icon={<AlertCircle className="h-8 w-8" />} message="Failed to load file" />
        ) : downloadUrl ? <VideoRenderer url={downloadUrl} /> : <Loading />;
      case 'markdown':
        return <TextRendererAsync backend={backend} entry={entry} kind="markdown" />;
      case 'csv':
        return <CsvRenderer backend={backend} entry={entry} />;
      case 'json':
        return <JsonRenderer backend={backend} entry={entry} />;
      case 'text':
        return <TextRendererAsync backend={backend} entry={entry} kind="text" />;
      default:
        return <FallbackBlock name={entry.name} downloadUrl={downloadUrl ?? undefined} />;
    }
  };

  if (derivatives.length === 0) {
    return <div className="flex-1 min-h-0 min-w-0 overflow-y-auto px-4 py-3">{renderSource()}</div>;
  }

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      className="flex-1 min-h-0 flex flex-col"
    >
      <TabsList className="rounded-none border-b border-border bg-transparent p-0 h-auto justify-start px-2">
        <TabsTrigger
          value="source"
          className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-2 text-xs"
        >
          Original
        </TabsTrigger>
        {derivatives.map((d) => (
          <TabsTrigger
            key={d.fileId}
            value={d.fileId}
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-2 text-xs"
          >
            {derivativeLabel(d)}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="source" className="m-0 flex-1 min-h-0 min-w-0 overflow-y-auto px-4 py-3">
        {renderSource()}
      </TabsContent>
      {derivatives.map((d) => (
        <TabsContent
          key={d.fileId}
          value={d.fileId}
          className="m-0 flex-1 min-h-0 min-w-0 overflow-y-auto px-4 py-3"
        >
          <DerivativeRenderer backend={backend} derivative={d} entry={entry} />
        </TabsContent>
      ))}
    </Tabs>
  );
}

function DerivativeRenderer({
  backend,
  derivative,
  entry,
}: {
  backend: FilesBackend;
  derivative: VfsDerivative;
  entry: VfsEntry;
}) {
  // Derivative metadata (type / size / created) lives in the bottom
  // footer now — see `PreviewFooter`. This renderer is just content.
  const toast = useToast();
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  // Text vs binary: the OCR / text-extract derivatives come back as
  // text/* (or JSON); thumbnails and other binary derivatives are
  // served via the /download endpoint as a presigned URL. The KB API
  // takes the ORIGINAL file's path + a `?derivative=<type>` query
  // param — never the s3Key, never a `.derivatives/...` synthesised
  // path.
  const isTextish =
    (derivative.contentType ?? '').startsWith('text/') ||
    derivative.contentType === 'application/json';
  const isImage = (derivative.contentType ?? '').startsWith('image/');

  // For binary previews (images), fetch the presigned URL.
  const [binaryUrl, setBinaryUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setContent(null);
    setBinaryUrl(null);
    if (isTextish) {
      backend
        .readText(entry.path, undefined, derivative.type)
        .then((text) => {
          if (!cancelled) setContent(text);
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err instanceof Error ? err : new Error(String(err)));
          }
        });
    } else if (isImage) {
      backend
        .getDownloadUrl(entry.path, derivative.type)
        .then((url) => {
          if (!cancelled) setBinaryUrl(url);
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err instanceof Error ? err : new Error(String(err)));
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [backend, derivative.type, entry.path, isImage, isTextish]);

  if (error) {
    return (
      <StatusBlock
        icon={<AlertCircle className="h-8 w-8" />}
        message={`Failed to load: ${error.message}`}
      />
    );
  }

  if (isTextish) {
    if (content === null) return <Loading />;
    return (
      <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
        {content}
      </pre>
    );
  }

  if (isImage) {
    if (!binaryUrl) return <Loading />;
    return (
      <img
        src={binaryUrl}
        alt={`${entry.name} · ${derivativeLabel(derivative)}`}
        className="max-w-full rounded border border-border"
      />
    );
  }

  // Fallback for unknown binary content types — offer a download.
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={async () => {
        try {
          const url = await backend.getDownloadUrl(entry.path, derivative.type);
          await downloadFileFromUrl(url, `${entry.name}.${derivative.type}`);
        } catch (err) {
          toast.error(
            err instanceof Error ? err.message : 'Failed to download derivative',
          );
        }
      }}
    >
      <Download className="mr-2 h-3.5 w-3.5" />
      Download
    </Button>
  );
}

// ─── Renderers ────────────────────────────────────────────────────────

function PdfRenderer({ url }: { url: string }) {
  return (
    <iframe
      src={url}
      title="PDF preview"
      className="w-full border-0 rounded"
      style={{ height: '60vh' }}
    />
  );
}

function ImageRenderer({ url, name }: { url: string; name: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <ImageOff className="h-8 w-8 mb-2" />
        <span className="text-sm">Failed to load image</span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center">
      <img
        src={url}
        alt={name}
        className="max-h-[55vh] max-w-full object-contain"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

function VideoRenderer({ url }: { url: string }) {
  return (
    <div className="flex items-center justify-center">
      <video controls className="max-h-[55vh] max-w-full">
        <source src={url} />
        Your browser does not support the video element.
      </video>
    </div>
  );
}

function TextRendererAsync({
  backend,
  entry,
  kind,
}: {
  backend: FilesBackend;
  entry: VfsEntry;
  kind: 'markdown' | 'text';
}) {
  const { content, error } = useFilesEntryText(backend, entry);

  if (error) return <StatusBlock icon={<AlertCircle className="h-8 w-8" />} message="Failed to load content" />;
  if (content === null) return <Loading />;

  if (kind === 'markdown') {
    return <MarkdownContent size="sm">{content}</MarkdownContent>;
  }

  const ext = entry.name.split('.').pop()?.toLowerCase() ?? '';
  let display = content;
  if (ext === 'json') {
    try { display = JSON.stringify(JSON.parse(content), null, 2); } catch { /* keep raw */ }
  }

  return (
    <CodeContent
      language={languageFromExt(ext)}
      label={ext ? ext.toUpperCase() : 'TEXT'}
      maxHeight="50vh"
    >
      {display}
    </CodeContent>
  );
}

// Minimal RFC-4180-ish CSV parser. Handles quoted fields, escaped quotes
// (""), embedded commas/newlines, and CRLF. Sufficient for in-browser preview;
// not a substitute for a real CSV library if we ever need dialect detection.
function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const all: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; continue; }
        inQuotes = false;
        continue;
      }
      field += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { cur.push(field); field = ''; continue; }
    if (ch === '\r') { continue; }
    if (ch === '\n') { cur.push(field); all.push(cur); cur = []; field = ''; continue; }
    field += ch;
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); all.push(cur); }
  if (all.length === 0) return { headers: [], rows: [] };
  return { headers: all[0], rows: all.slice(1) };
}

function CsvRenderer({ backend, entry }: { backend: FilesBackend; entry: VfsEntry }) {
  const { content, error } = useFilesEntryText(backend, entry);
  if (error) return <StatusBlock icon={<AlertCircle className="h-8 w-8" />} message="Failed to load content" />;
  if (content === null) return <Loading />;
  return <CsvTabs content={content} />;
}

function CsvTabs({ content }: { content: string }) {
  const parsed = useMemo(() => parseCsv(content), [content]);

  // Bridge parsed CSV to the AuiTable contract:
  //   - columns: [{ header, key }]
  //   - rows: Record<columnKey, string>
  // Headers without a name get a synthesized key so we don't collide on
  // empty strings. The node id stays constant across files — AuiTable
  // self-corrects when columns change, and persistence falls back to
  // local-only state outside a surface, so re-using the id is safe.
  const tableNode = useMemo<AuiNode>(() => {
    const colKey = (header: string, i: number) => header || `__col${i}`;
    const columns = parsed.headers.map((h, i) => ({
      header: h || `Column ${i + 1}`,
      key: colKey(h, i),
    }));
    const rows = parsed.rows.map((row) => {
      const obj: Record<string, string> = {};
      parsed.headers.forEach((h, i) => {
        obj[colKey(h, i)] = row[i] ?? '';
      });
      return obj;
    });
    return {
      id: 'csv-preview-table',
      component: { type: 'Table', props: { columns, rows, interactive: true } },
    };
  }, [parsed]);

  return (
    <Tabs defaultValue="table">
      <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
        <TabsTrigger
          value="table"
          className="relative rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
        >
          Table
        </TabsTrigger>
        <TabsTrigger
          value="raw"
          className="relative rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
        >
          Raw
        </TabsTrigger>
      </TabsList>

      <TabsContent value="table" className="mt-4">
        {parsed.headers.length === 0 ? (
          <StatusBlock icon={<FileText className="h-8 w-8" />} message="Empty CSV" />
        ) : (
          <AuiTable node={tableNode} />
        )}
      </TabsContent>

      <TabsContent value="raw" className="mt-4">
        <CodeContent language="csv" label="CSV" maxHeight="50vh">
          {content}
        </CodeContent>
      </TabsContent>
    </Tabs>
  );
}

function JsonRenderer({ backend, entry }: { backend: FilesBackend; entry: VfsEntry }) {
  const { content, error } = useFilesEntryText(backend, entry);
  if (error) return <StatusBlock icon={<AlertCircle className="h-8 w-8" />} message="Failed to load content" />;
  if (content === null) return <Loading />;
  return <JsonContent>{content}</JsonContent>;
}

function FallbackBlock({ name, downloadUrl }: { name: string; downloadUrl?: string }) {
  const toast = useToast();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <FileText className="h-12 w-12 mb-3" />
      <p className="text-sm font-medium mb-1">Preview not available</p>
      <p className="text-xs mb-4">{name}</p>
      {downloadUrl && (
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => {
            try {
              await downloadFileFromUrl(downloadUrl, name);
            } catch {
              toast.error(`Failed to download ${name}`);
            }
          }}
        >
          <Download className="h-4 w-4 mr-1.5" />
          Download File
        </Button>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

function StatusBlock({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      {icon}
      <p className="text-sm mt-2">{message}</p>
    </div>
  );
}
